import UserManager, { CAPTURE_EMAIL_SOURCE } from '../../services/UserManager'
import { getAuthRequestConfig } from '../../services/LoginUtills'
import { logger } from '../../services/logger'
import { shippingOptionsManager } from '../../services/ShippingOptionsManager'
import { ExternalToPaymentMethod } from '../StripePaymentForm/StripePaymentForm.utils'
import { createAddressFromStripeFormat } from '../StripeAddressForm/stripeAdapter'
import { type CheckoutContextValue } from './CheckoutContext'
import type {
  ExpressCheckoutConfirmData,
  ExpressCheckoutConfirmResult,
} from '../StripeExpressCheckout/StripeExpressCheckout'
import { type CreateOrderParams } from '../../services/OrderManager.types'

interface ConfirmExpressCheckoutOrderDeps {
  checkout: CheckoutContextValue
  getToken: () => Promise<string>
  shouldValidateAddressInServer: boolean
  callCreateOrder: (params: Omit<CreateOrderParams, 'bagAnalytics'>) => void
}

export async function confirmExpressCheckoutOrder(
  {
    checkout,
    getToken,
    shouldValidateAddressInServer,
    callCreateOrder,
  }: ConfirmExpressCheckoutOrderDeps,
  data: ExpressCheckoutConfirmData
): Promise<ExpressCheckoutConfirmResult> {
  const { confirmationTokenId, billingDetails, expressPaymentType } = data
  const expressCheckoutQuote =
    data.addressSource === 'express-checkout' ? data.quote : undefined
  const groupCheckoutId = expressCheckoutQuote
    ? expressCheckoutQuote.summary.checkoutId
    : checkout.groupCheckoutId

  if (checkout.isGroupOrder && !groupCheckoutId) {
    logger.error(
      'Express checkout order failed: group order missing checkoutId',
      null
    )
    return { ok: false, reason: 'fail' }
  }

  const paymentMethodType = ExternalToPaymentMethod[expressPaymentType]
  if (!paymentMethodType) {
    logger.error(
      'Express checkout order failed: unmapped express payment type',
      null,
      {
        expressPaymentType,
      }
    )
    return { ok: false, reason: 'fail' }
  }

  const address =
    data.addressSource === 'express-checkout'
      ? createAddressFromStripeFormat({
          ...data.shippingAddress,
          phone: billingDetails?.phone,
        })
      : checkout.address

  const email = billingDetails?.email
  if (email) {
    await UserManager.setUserEmail({
      email,
      source: CAPTURE_EMAIL_SOURCE.checkoutPayment,
      onboardingFlow: undefined,
      refetchExperiments: false,
      emailConsent: UserManager.hasExplicitEmailConsent(email),
    })
  }

  const deliveryPromise = expressCheckoutQuote
    ? expressCheckoutQuote.shippingOption
    : checkout.selectedShippingOption
  // The wallet sheet's quote carries the dates it fetched for the address the customer picked.
  const hasUpToDateDeliveryDates = expressCheckoutQuote
    ? true
    : checkout.hasUpToDateDeliveryDates
  const token = await getToken()

  if (expressCheckoutQuote) {
    shippingOptionsManager.recordShippingOptionsShownForOrder(
      expressCheckoutQuote.shippingOptions
    )
  }

  callCreateOrder({
    tiles: checkout.orderTiles,
    orderItems: checkout.orderItems,
    isGroupOrder: checkout.isGroupOrder,
    groupCheckoutId,
    deliveryPromise,
    hasUpToDateDeliveryDates,
    deliveryRange: checkout.getDeliveryRange(deliveryPromise),
    address: address && { ...address, source: data.addressSource },
    paymentMethod: {
      type: paymentMethodType,
    },
    paymentMethodId: null,
    expressCheckoutConfirmationToken: confirmationTokenId,
    orderPriceSummary: expressCheckoutQuote
      ? expressCheckoutQuote.summary
      : checkout.pricingSummary,
    shouldValidateAddress: shouldValidateAddressInServer,
    requestConfig: getAuthRequestConfig(token),
    topazLabsEnabled: false,
  })

  return { ok: true }
}
